<template>
  <section>
    <Alert
      data-testid="alert"
      v-if="showAlert"
      :message="alertMessage"
      :alertType="alertType"
      :closable="true"
      @onClose="dismissAlert"
    />

    <div v-if="showNoOffers">
      <NoOffers data-testid="no-offers" />
    </div>
  </section>
</template>

<script setup lang="ts">
import NoOffers from "@/apps/merchant3/shared-components/common/NoOffers.vue";
import { computed, onUnmounted, ref } from "vue";
import { getPersonFraudAssessment } from "@/apps/merchant3/services/persons.service";
import Alert from "@/apps/merchant3/shared-components/common/Alert.vue";
import { getAPIErrorMessage } from "@/apps/merchant3/shared-helpers/APIErrorMessages";
import IErrorResponse from "@/apps/merchant3/types/IErrorResponse";
import { useStore } from "vuex";
import { useI18n } from "@/apps/merchant3/composables/useI18n";

const { t } = useI18n();
const showNoOffers = ref<boolean>(false);
const alertMessage = ref("");
const alertType = ref("");
const showAlert = ref(false);
const dismissAlert = () => {
  showAlert.value = false;
};

type IFraudAssessmentResult = "VALID" | "INVALID" | "UNAVAILABLE";
type IFraudAssessmentStatus = "INPROGRESS" | "COMPLETED" | "UNAVAILABLE";
const emit = defineEmits<{
  (e: "fraud-assessment-result", result: IFraudAssessmentResult): void;
  (e: "loading", enabled: boolean): void;
}>();

const currentFraudAssessmentResult = ref<IFraudAssessmentResult | null>(null);
const currentFraudAssessmentStatus = ref<IFraudAssessmentStatus | null>(null);
let pollingFraudAssessmentInterval: ReturnType<typeof setInterval> | null =
  null;
const handleFraudAssessmentResponse = (
  result: IFraudAssessmentResult,
  status: IFraudAssessmentStatus
) => {
  currentFraudAssessmentResult.value = result;
  currentFraudAssessmentStatus.value = status;
  if (status === "COMPLETED" && (result === "VALID" || result === "INVALID")) {
    emit("fraud-assessment-result", result);
    if (result === "INVALID") {
      showNoOffers.value = true;
    }
    stopPollingFraudAssessment();
  }
};

const store = useStore();
const cartTag = computed(() => store.state.carts?.cartTag || "");
let pollAttempts = 0;
const MAX_ATTEMPTS = 12; // 12 tries * 5 secs per try = 60secs max wait time
const isPolling = ref<boolean>(false);
const pollFraudAssessmentStatus = async () => {
  pollAttempts++;
  if (isPolling.value) return;
  isPolling.value = true;
  try {
    const response = await getPersonFraudAssessment(cartTag.value);
    handleFraudAssessmentResponse(response.data?.result, response.data?.status);

    if (pollAttempts >= MAX_ATTEMPTS) {
      stopPollingFraudAssessment({ errorCode: 429 });
    }
  } catch (error: unknown) {
    stopPollingFraudAssessment(error?.data);
  } finally {
    isPolling.value = false;
  }
};

const startPollingFraudAssessment = () => {
  if (isPolling.value) return;
  emit("loading", true);
  pollFraudAssessmentStatus(); // call immediately once
  pollingFraudAssessmentInterval = setInterval(
    pollFraudAssessmentStatus,
    5000 // 5-10 seconds polling recommended by documentation of integration
  );
};

const stopPollingFraudAssessment = (error?: IErrorResponse) => {
  if (pollingFraudAssessmentInterval) {
    clearInterval(pollingFraudAssessmentInterval);
    pollingFraudAssessmentInterval = null;
    isPolling.value = false;
    emit("loading", false);
  }

  if (error) {
    alertType.value = "alert-danger";
    alertMessage.value = getAPIErrorMessage(error, t);
    showAlert.value = true;
    emit("fraud-assessment-result", "INVALID");
    showNoOffers.value = true;
  }
};

defineExpose({
  start: startPollingFraudAssessment,
});
onUnmounted(() => {
  stopPollingFraudAssessment();
});
</script>
